import { useState, useEffect, useCallback } from 'react';
import { API } from '../App';

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications(evaluator) {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [supported, setSupported] = useState(true);

  // Check if push notifications are supported
  useEffect(() => {
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    setSupported(isSupported);
    
    if (isSupported && 'Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker and get existing subscription
  useEffect(() => {
    if (!supported || !evaluator || evaluator.isPlayer) return;

    const checkSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const existingSub = await registration.pushManager.getSubscription();
        setSubscription(existingSub);
      } catch (err) {
        console.error('Error checking subscription:', err);
      }
    };

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(() => checkSubscription())
        .catch(err => console.error('SW registration failed:', err));
    }
  }, [supported, evaluator]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!supported || loading) return;

    setLoading(true);
    setError(null);

    try {
      // Request notification permission
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        setError('Permissão de notificação negada');
        setLoading(false);
        return;
      }

      // Get VAPID public key from server
      const keyResponse = await fetch(`${API}/notifications/vapid-public-key`);
      if (!keyResponse.ok) {
        throw new Error('Falha ao obter chave do servidor');
      }
      const { publicKey } = await keyResponse.json();

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push manager
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to server
      const response = await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: pushSubscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(pushSubscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(pushSubscription.getKey('auth'))))
          },
          evaluator_id: evaluator?.id || null
        })
      });

      if (!response.ok) {
        throw new Error('Falha ao registrar assinatura');
      }

      setSubscription(pushSubscription);

      // Send test notification
      await fetch(`${API}/notifications/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: pushSubscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(pushSubscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(pushSubscription.getKey('auth'))))
          }
        })
      });

    } catch (err) {
      console.error('Push subscription error:', err);
      setError(err.message || 'Erro ao ativar notificações');
    } finally {
      setLoading(false);
    }
  }, [supported, loading, evaluator]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription || loading) return;

    setLoading(true);
    setError(null);

    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Remove subscription from server
      await fetch(`${API}/notifications/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });

      setSubscription(null);
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError(err.message || 'Erro ao desativar notificações');
    } finally {
      setLoading(false);
    }
  }, [subscription, loading]);

  return {
    supported,
    permission,
    subscription,
    loading,
    error,
    isSubscribed: !!subscription,
    subscribe,
    unsubscribe
  };
}
